import CheckIcon from '@mui/icons-material/Check';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import SearchIcon from '@mui/icons-material/Search';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React, { useEffect, useRef, useState } from 'react';

import {
  CheckmarkBox,
  EmptyText,
  LoadingBox,
  MemberAvatar,
  MemberInfoBox,
  MemberMenuItem,
  MemberMenuList,
  PendingSpinner,
  PickerPaper,
  PickerPopper,
  PickerTriggerButton,
  SearchBox,
  SectionLabel,
  SectionLabelBox,
} from './MemberPickerDropdown.styles';

export interface MemberPickerItem {
  userId: string;
  name: string;
  email: string;
  avatarUrl?: string;
}

export interface MemberPickerDropdownProps {
  members: MemberPickerItem[];
  selectedIds: Set<string>;
  onToggle: (member: MemberPickerItem) => void;
  isBusy?: (userId: string) => boolean;
  isLoading?: boolean;
  buttonLabel?: string;
  listLabel?: string;
  placement?: 'bottom-start' | 'bottom-end' | 'bottom';
  onOpen?: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  anchorEl?: HTMLElement | null;
  hideTrigger?: boolean;
}

const MemberPickerDropdown: React.FC<MemberPickerDropdownProps> = ({
  members,
  selectedIds,
  onToggle,
  isBusy = () => false,
  isLoading = false,
  buttonLabel = 'Add members',
  listLabel = 'WORKSPACE MEMBERS',
  placement = 'bottom-start',
  onOpen,
  open: openProp,
  onOpenChange,
  anchorEl: anchorElProp,
  hideTrigger = false,
}) => {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [internalOpen, setInternalOpen] = useState(false);
  const [search, setSearch] = useState('');

  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : internalOpen;
  const effectiveAnchor = anchorElProp !== undefined ? anchorElProp : anchorRef.current;

  const setOpen = (value: boolean) => {
    if (isControlled) onOpenChange?.(value);
    else setInternalOpen(value);
    if (!value) setSearch('');
  };

  const handleOpen = () => setOpen(!open);

  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open && !wasOpenRef.current) onOpen?.();
    wasOpenRef.current = open;
  }, [open, onOpen]);

  const filtered = members.filter(
    (m) =>
      m.name.toLowerCase().includes(search.toLowerCase()) ||
      m.email.toLowerCase().includes(search.toLowerCase()),
  );

  // Sort: selected first, then alphabetical
  const sorted = [...filtered].sort((a, b) => {
    const aChecked = selectedIds.has(a.userId);
    const bChecked = selectedIds.has(b.userId);
    if (aChecked !== bChecked) return aChecked ? -1 : 1;
    return a.name.localeCompare(b.name);
  });

  return (
    <>
      {!hideTrigger && (
        <PickerTriggerButton ref={anchorRef} startIcon={<PersonAddAlt1Icon />} onClick={handleOpen}>
          {buttonLabel}
        </PickerTriggerButton>
      )}

      <PickerPopper open={open} anchorEl={effectiveAnchor} placement={placement}>
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <PickerPaper elevation={0}>
            <SearchBox>
              <TextField
                autoFocus
                fullWidth
                size="small"
                placeholder="Search members..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </SearchBox>

            <SectionLabelBox>
              <SectionLabel variant="caption">{listLabel}</SectionLabel>
            </SectionLabelBox>

            <Divider sx={{ mx: 1.5, mb: 0.5 }} />

            {isLoading ? (
              <LoadingBox>
                <PendingSpinner color="secondary" />
              </LoadingBox>
            ) : sorted.length === 0 ? (
              <EmptyText variant="body2">No members found</EmptyText>
            ) : (
              <MemberMenuList dense>
                {sorted.map((member) => {
                  const checked = selectedIds.has(member.userId);
                  const pending = isBusy(member.userId);
                  return (
                    <MemberMenuItem
                      key={member.userId}
                      selected={checked}
                      onClick={() => !pending && onToggle(member)}
                    >
                      <MemberAvatar src={member.avatarUrl} alt={member.name}>
                        {member.name.charAt(0).toUpperCase()}
                      </MemberAvatar>
                      <MemberInfoBox>
                        <Typography
                          variant="body2"
                          noWrap
                          fontWeight={checked ? 600 : 400}
                          fontSize="0.8125rem"
                        >
                          {member.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                          fontSize="0.6875rem"
                        >
                          {member.email}
                        </Typography>
                      </MemberInfoBox>
                      {pending ? (
                        <PendingSpinner color="secondary" />
                      ) : (
                        <CheckmarkBox checked={checked}>
                          {checked && <CheckIcon sx={{ fontSize: 10, color: '#fff' }} />}
                        </CheckmarkBox>
                      )}
                    </MemberMenuItem>
                  );
                })}
              </MemberMenuList>
            )}
          </PickerPaper>
        </ClickAwayListener>
      </PickerPopper>
    </>
  );
};

export default MemberPickerDropdown;
